import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  Delete,
  FilterList,
  ExpandMore,
  Clear,
} from '@mui/icons-material';
import React, { useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { CsvDataType, CsvSchemaResponse } from '@/api/gen/model';

interface Filter {
  columnId: number;
  operator: number;
  value: any;
}

interface GenerarFiltrosProps {
  csvSchema: CsvSchemaResponse;
  onApplyFilters: (filterRequest: any) => void;
  isLoading?: boolean;
}

// Mapeo de operadores según el tipo de dato
const getOperatorsByDataType = (dataType?: CsvDataType) => {
  switch (dataType) {
    case CsvDataType.String:
      return [
        { value: 0, label: 'Igual (==)' },
        { value: 1, label: 'Diferente (!=)' },
        { value: 6, label: 'Contiene' },
        { value: 7, label: 'Empieza con' },
        { value: 8, label: 'Termina con' },
      ];
    case CsvDataType.Int:
    case CsvDataType.Double:
      return [
        { value: 0, label: 'Igual (==)' },
        { value: 1, label: 'Diferente (!=)' },
        { value: 2, label: 'Mayor que (>)' },
        { value: 3, label: 'Mayor o igual (>=)' },
        { value: 4, label: 'Menor que (<)' },
        { value: 5, label: 'Menor o igual (<=)' },
      ];
    case CsvDataType.DateTime:
      return [
        { value: 0, label: 'Igual' },
        { value: 1, label: 'Diferente' },
        { value: 2, label: 'Posterior a (>)' },
        { value: 3, label: 'A partir de (>=)' },
        { value: 4, label: 'Anterior a (<)' },
        { value: 5, label: 'Hasta (<=)' },
      ];
    case CsvDataType.Bool:
      return [
        { value: 0, label: 'Igual' },
        { value: 1, label: 'Diferente' },
      ];
    default:
      return [{ value: 0, label: 'Igual' }];
  }
};

export default function GenerarFiltros({
  csvSchema,
  onApplyFilters,
  isLoading,
}: GenerarFiltrosProps) {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [logicOperator, setLogicOperator] = useState<number>(0); // 0 = AND, 1 = OR
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [orderByDescending, setOrderByDescending] = useState<boolean>(true);

  const handleAddFilter = () => {
    const firstColumn = csvSchema.columns?.[0];
    if (firstColumn) {
      setFilters([
        ...filters,
        {
          columnId: firstColumn.id!,
          operator: 0,
          value: '',
        },
      ]);
    }
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleFilterChange = (
    index: number,
    field: keyof Filter,
    value: any
  ) => {
    const newFilters = [...filters];
    newFilters[index] = {
      ...newFilters[index],
      [field]: value,
    };

    // Si cambia la columna, resetear el operador y valor
    if (field === 'columnId') {
      newFilters[index].operator = 0;
      newFilters[index].value = '';
    }

    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters([]);
    setFromDate(null);
    setToDate(null);
    setLogicOperator(0);
  };

  const handleApplyFilters = () => {
    const filterRequest: any = {
      csvSchemaId: csvSchema.id,
      filters: filters.map(f => {
        // Convertir valores Date a ISO string para DateTime
        const column = csvSchema.columns?.find(c => c.id === f.columnId);
        let value = f.value;

        if (
          column?.dataType === CsvDataType.DateTime &&
          f.value instanceof Date
        ) {
          value = f.value.toISOString();
        }

        return {
          columnId: f.columnId,
          operator: f.operator,
          value: value,
        };
      }),
      logicOperator,
      orderByDescending,
    };

    if (fromDate) {
      filterRequest.fromDate = fromDate.toISOString();
    }
    if (toDate) {
      filterRequest.toDate = toDate.toISOString();
    }

    onApplyFilters(filterRequest);
  };

  const getColumnDataType = (columnId: number): CsvDataType | undefined => {
    const column = csvSchema.columns?.find(c => c.id === columnId);
    return column?.dataType;
  };

  const renderValueInput = (filter: Filter, index: number) => {
    const dataType = getColumnDataType(filter.columnId);

    switch (dataType) {
      case CsvDataType.Bool:
        return (
          <FormControl fullWidth size='small'>
            <Select
              value={filter.value === '' ? '' : String(filter.value)}
              onChange={e => {
                const val = e.target.value;
                handleFilterChange(
                  index,
                  'value',
                  val === 'true' ? true : val === 'false' ? false : ''
                );
              }}
            >
              <MenuItem value=''>Seleccionar...</MenuItem>
              <MenuItem value='true'>Verdadero</MenuItem>
              <MenuItem value='false'>Falso</MenuItem>
            </Select>
          </FormControl>
        );

      case CsvDataType.DateTime:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DateTimePicker
              value={
                filter.value instanceof Date
                  ? filter.value
                  : filter.value
                    ? new Date(filter.value)
                    : null
              }
              onChange={newValue =>
                handleFilterChange(index, 'value', newValue || null)
              }
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        );

      case CsvDataType.Int:
      case CsvDataType.Double:
        return (
          <TextField
            fullWidth
            size='small'
            type='number'
            value={filter.value}
            onChange={e =>
              handleFilterChange(
                index,
                'value',
                dataType === CsvDataType.Int
                  ? parseInt(e.target.value)
                  : parseFloat(e.target.value)
              )
            }
            placeholder='Ingrese valor numérico'
            inputProps={{
              step: dataType === CsvDataType.Double ? '0.01' : '1',
            }}
          />
        );

      case CsvDataType.String:
      default:
        return (
          <TextField
            fullWidth
            size='small'
            value={filter.value}
            onChange={e => handleFilterChange(index, 'value', e.target.value)}
            placeholder='Ingrese texto'
          />
        );
    }
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          <Typography variant='h6'>Filtros de Búsqueda</Typography>
          {filters.length > 0 && (
            <Chip
              label={`${filters.length} filtros`}
              size='small'
              color='primary'
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Filtros dinámicos por columna */}
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography variant='subtitle1' fontWeight='bold'>
                Filtros por Columna
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  disabled={filters.length === 0}
                >
                  Limpiar
                </Button>
                <Button
                  variant='contained'
                  size='small'
                  startIcon={<Add />}
                  onClick={handleAddFilter}
                >
                  Añadir Filtro
                </Button>
              </Box>
            </Box>

            {filters.map((filter, index) => {
              const selectedColumn = csvSchema.columns?.find(
                c => c.id === filter.columnId
              );
              const operators = getOperatorsByDataType(
                selectedColumn?.dataType
              );

              return (
                <Paper
                  key={index}
                  variant='outlined'
                  sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}
                >
                  <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Columna</InputLabel>
                        <Select
                          value={filter.columnId}
                          label='Columna'
                          onChange={e =>
                            handleFilterChange(
                              index,
                              'columnId',
                              e.target.value
                            )
                          }
                        >
                          {csvSchema.columns?.map(column => (
                            <MenuItem key={column.id} value={column.id}>
                              {column.header} ({column.dataType})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Operador</InputLabel>
                        <Select
                          value={filter.operator}
                          label='Operador'
                          onChange={e =>
                            handleFilterChange(
                              index,
                              'operator',
                              e.target.value
                            )
                          }
                        >
                          {operators.map(op => (
                            <MenuItem key={op.value} value={op.value}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      {renderValueInput(filter, index)}
                    </Grid>

                    <Grid item xs={12} sm={1}>
                      <IconButton
                        color='error'
                        onClick={() => handleRemoveFilter(index)}
                        title='Eliminar filtro'
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}

            {filters.length === 0 && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ textAlign: 'center', py: 2 }}
              >
                No hay filtros. Haga clic en "Añadir Filtro" para empezar.
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Opciones de filtrado */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={logicOperator === 1}
                    onChange={e => setLogicOperator(e.target.checked ? 1 : 0)}
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='bold'>
                      Operador Lógico
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {logicOperator === 0
                        ? 'AND (Todos)'
                        : 'OR (Al menos uno)'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={orderByDescending}
                    onChange={e => setOrderByDescending(e.target.checked)}
                    color='secondary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='bold'>
                      Orden
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {orderByDescending ? 'Descendente' : 'Ascendente'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Filtros de fecha opcionales */}
          <Box>
            <Typography variant='subtitle2' gutterBottom fontWeight='bold'>
              Filtros de Timestamp (Opcional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={es}
                >
                  <DateTimePicker
                    label='Desde'
                    value={fromDate}
                    onChange={newValue => setFromDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={es}
                >
                  <DateTimePicker
                    label='Hasta'
                    value={toDate}
                    onChange={newValue => setToDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>

          {/* Botón Aplicar */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='contained'
              size='large'
              startIcon={<FilterList />}
              onClick={handleApplyFilters}
              disabled={isLoading}
              sx={{ px: 4 }}
            >
              {isLoading ? 'Aplicando...' : 'Aplicar Filtros'}
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
